import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import WTypography from 'Components/whiteTypography'
import { Radar } from 'react-chartjs-2'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditIcon from '@mui/icons-material/Edit'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import { makePostRequest } from 'utils/api'

const wrapLabel = (label) => {
  const words = String(label || '').split(' ')
  const lines = []
  let currentLine = ''

  words.forEach((word) => {
    if ((currentLine + ' ' + word).trim().length <= 14) {
      currentLine += ` ${word}`
    } else {
      lines.push(currentLine.trim())
      currentLine = word
    }
  })

  if (currentLine) {
    lines.push(currentLine.trim())
  }

  return lines
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 24,
  },
  scales: {
    r: {
      min: 0,
      max: 100,
      grid: {
        color: 'gray',
        lineWidth: 1,
      },
      angleLines: {
        color: 'gray',
      },
      pointLabels: {
        color: 'white',
        font: {
          size: 13,
        },
        callback: wrapLabel,
      },
      ticks: {
        stepSize: 20,
        display: true,
        color: 'black',
      },
    },
  },
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
      position: 'top',
    },
  },
}

function Competences({ oldData, updateReports }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [assessment, setAssessment] = useState(oldData || {})
  const [open, setOpen] = useState(false)
  const [scores, setScores] = useState([3, 3, 3, 3, 3])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setAssessment(oldData || {})
  }, [oldData])

  useEffect(() => {
    if (assessment?.raw_scores?.length === 5) {
      setScores(assessment.raw_scores.map((score) => score || 3))
    }
  }, [assessment])

  const chartData = useMemo(
    () => ({
      labels: assessment?.labels?.length
        ? assessment.labels
        : [
            t('professionnelle.competence1'),
            t('professionnelle.competence2'),
            t('professionnelle.competence3'),
            t('professionnelle.competence4'),
            t('professionnelle.competence5'),
          ],
      datasets: [
        {
          data: assessment?.data?.length ? assessment.data : [0, 0, 0, 0, 0],
          fill: true,
          borderColor: '#c1daf1',
          pointBackgroundColor: '#c1daf1',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#c1daf1',
        },
      ],
    }),
    [assessment, t],
  )

  const generateSkills = async () => {
    setLoading(true)
    setError('')
    const response = await makePostRequest(
      '/api/Professional/competence-assessment/',
      { action: 'generate' },
      {},
      true,
    )
    setLoading(false)

    if (response?.status === 200) {
      setAssessment(response.data)
      setScores(response.data.raw_scores.map((score) => score || 3))
      setOpen(true)
      updateReports()
    } else {
      setError(response?.data?.message || t('professionnelle.errCannotGenerate') /* LAP-733 (GE022) */)
    }
  }

  const saveScores = async () => {
    setLoading(true)
    setError('')
    const response = await makePostRequest(
      '/api/Professional/competence-assessment/',
      { action: 'save_scores', scores },
      {},
      true,
    )
    setLoading(false)

    if (response?.status === 200) {
      setAssessment(response.data)
      setOpen(false)
      updateReports()
    } else {
      setError(response?.data?.message || t('professionnelle.errCannotSave') /* LAP-733 (GE022) */)
    }
  }

  const openAssessment = async () => {
    if (assessment?.show) {
      setOpen(true)
      return
    }
    await generateSkills()
  }

  const needsProfile = assessment?.missing_profile
  const hasSkills = assessment?.show

  return (
    <>
      <Card className="dashboard_card__root1" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box className="dashboard_card__header1 no-drag">
          <Box>
            <WTypography component="span" variant="h6">
              {t('professionnelle.competences') /* LAP-733 (GE022) */}
            </WTypography>
            {assessment?.current_job && (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.62)', fontSize: 13, mt: 0.5 }}>
                {t('professionnelle.referentielFor', { job: assessment.current_job }) /* LAP-733 (GE022) */}
              </Typography>
            )}
          </Box>
        </Box>

        {needsProfile ? (
          <Stack
            className="no-drag"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            spacing={1.5}
            sx={{ flex: 1, minHeight: 300, px: 2, color: 'white' }}
          >
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.16)',
              }}
            >
              <WorkOutlineIcon sx={{ color: '#c1daf1', fontSize: 36 }} />
            </Box>
            <Typography sx={{ fontWeight: 850, fontSize: 20 }}>
              {t('professionnelle.noJobDefined') /* LAP-733 (GE022) */}
            </Typography>
            <Typography sx={{ maxWidth: 350, color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
              {t('professionnelle.noJobDefinedDesc') /* LAP-733 (GE022) */}
            </Typography>
            <Button
              component={RouterLink}
              to="/professionnelle"
              variant="contained"
              sx={{
                bgcolor: '#1b75d0',
                borderRadius: 999,
                px: 2.6,
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': { bgcolor: '#145da8' },
              }}
            >
              {t('professionnelle.btnCompleteProfile') /* LAP-733 (GE022) */}
            </Button>
          </Stack>
        ) : (
          <>
            <Box mt={1} minWidth={3} mb={0} height="100%" sx={{ minHeight: 260 }}>
              {hasSkills ? (
                <Radar data={chartData} options={options} />
              ) : (
                <Stack
                  className="no-drag"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  spacing={1.5}
                  sx={{ height: '100%', minHeight: 260, color: 'white', px: 2 }}
                >
                  <AutoAwesomeIcon sx={{ color: '#c1daf1', fontSize: 42 }} />
                  <Typography sx={{ fontWeight: 850, fontSize: 20 }}>
                    {t('professionnelle.competencesToGenerate') /* LAP-733 (GE022) */}
                  </Typography>
                  <Typography sx={{ maxWidth: 360, color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
                    {t('professionnelle.competencesToGenerateDesc') /* LAP-733 (GE022) */}
                  </Typography>
                </Stack>
              )}
            </Box>

            {assessment?.needs_generation && hasSkills && (
              <Typography
                className="no-drag"
                sx={{ color: '#f8d36b', fontSize: 12.5, textAlign: 'center', mb: 0.5 }}
              >
                {t('professionnelle.competencesJobChanged') /* LAP-733 (GE022) */}
              </Typography>
            )}

            {error && (
              <Typography className="no-drag" sx={{ color: '#ffb4b4', fontSize: 13, textAlign: 'center' }}>
                {error}
              </Typography>
            )}

            <Box
              mt={0}
              className="dashboard_card__filters no-drag"
              sx={{ position: 'unset !important', width: '100%', pr: '0px !important' }}
            >
              <Button
                onClick={assessment?.needs_generation ? generateSkills : openAssessment}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={15} sx={{ color: 'white' }} />
                  ) : assessment?.needs_generation || !hasSkills ? (
                    <AutoAwesomeIcon />
                  ) : (
                    <EditIcon />
                  )
                }
                sx={{
                  mx: 'auto',
                  color: 'white',
                  fontWeight: 850,
                  textTransform: 'none',
                }}
              >
                {assessment?.needs_generation || !hasSkills
                  ? t('professionnelle.btnGenerateSkills') /* LAP-733 (GE022) */
                  : t('professionnelle.btnReevaluerCompetences') /* LAP-733 (GE022) */}
              </Button>
            </Box>
          </>
        )}
      </Card>

      <Dialog
        fullWidth
        maxWidth="sm"
        className="no-drag"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', bgcolor: '#f7f9fc' } }}
      >
        <DialogTitle sx={{ color: '#ffffff !important', bgcolor: '#06295f', px: 4, py: 3 }}>
          <Typography sx={{ fontWeight: 850, fontSize: 23 }}>
            {t('professionnelle.evalCompetences') /* LAP-733 (GE022) */}
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.72)', fontSize: 14, mt: 0.75 }}>
            {t('professionnelle.evalCompetencesDesc') /* LAP-733 (GE022) */}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Stack spacing={2.5}>
            {assessment?.skills?.map((skill, index) => (
              <Box key={skill.index}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                  <Typography sx={{ color: '#12233d', fontWeight: 800 }}>{skill.name}</Typography>
                  <Typography sx={{ color: '#1b75d0', fontWeight: 900 }}>{scores[index]} / 5</Typography>
                </Stack>
                <Slider
                  value={scores[index]}
                  min={1}
                  max={5}
                  step={1}
                  marks
                  onChange={(event, value) => {
                    setScores((previousScores) => {
                      const nextScores = [...previousScores]
                      nextScores[index] = value
                      return nextScores
                    })
                  }}
                  sx={{ color: '#1b75d0' }}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, gap: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#526173', fontWeight: 700 }}>
            {t('professionnelle.cancel') /* LAP-733 (GE022) */}
          </Button>
          <Button
            onClick={saveScores}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#1b75d0',
              borderRadius: 999,
              px: 3,
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': { bgcolor: '#145da8' },
            }}
          >
            {t('professionnelle.validate') /* LAP-733 (GE022) */}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Competences
